import React, { useEffect, useState } from 'react';
import { getPatientPayableClaims } from '../services/paymentsService.js';
import ClaimSelect from './ClaimSelect';

// Patients on an insurance invoice. A carrier often pays several patients'
// claims with one card, so each row is one patient, the claim the money is
// for, and that patient's share. Each row posts to the PMS as its own
// insurance payment on its claim.

export const rowsTotal = (rows) => rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

const input = {
  width: '100%',
  boxSizing: 'border-box',
  height: '36px',
  padding: '0 8px',
  border: '1px solid #e3e7e4',
  borderRadius: '4px',
  fontSize: '14px',
  backgroundColor: 'white',
};

// Patient | claim | amount | remove — shared by the header and each row.
const GRID = {
  display: 'grid',
  gridTemplateColumns: 'minmax(160px, 1fr) minmax(220px, 2fr) 140px 36px',
  gap: '10px',
  alignItems: 'center',
};

const InsuranceAllocations = ({ practiceId, rows, onChange, autoPostOn }) => {
  // Claims per patient id: { loading, claims }.
  const [claimsByPatient, setClaimsByPatient] = useState({});

  useEffect(() => {
    rows.forEach(({ patientId }) => {
      if (!patientId || claimsByPatient[patientId]) return;
      setClaimsByPatient((m) => ({ ...m, [patientId]: { loading: true, claims: [] } }));
      getPatientPayableClaims(practiceId, patientId)
        .then((data) => setClaimsByPatient((m) => ({ ...m, [patientId]: { loading: false, claims: data?.claims || [] } })))
        .catch(() => setClaimsByPatient((m) => ({ ...m, [patientId]: { loading: false, claims: [] } })));
    });
  }, [rows, practiceId, claimsByPatient]);

  const update = (key, field, value) => onChange(rows.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  const remove = (key) => onChange(rows.filter((r) => r.key !== key));
  // A claim belongs on one row only.
  const claimsInUse = new Set(rows.map((r) => r.claim).filter(Boolean));

  if (rows.length === 0) {
    return (
      <div style={{ fontSize: '13px', color: '#6b716d', marginBottom: '20px' }}>
        Search above to add each patient this insurance payment covers.
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
        Patients on this payment ({rows.length})
      </div>
      <div style={{ ...GRID, fontSize: '12px', color: '#6b716d', marginBottom: '4px' }}>
        <span>Patient</span>
        <span>Claim</span>
        <span>Amount</span>
        <span />
      </div>
      {rows.map((row) => {
        const entry = claimsByPatient[row.patientId] || { loading: true, claims: [] };
        return (
          <div key={row.key} style={{ ...GRID, marginBottom: '8px' }}>
            <div style={{ minWidth: 0 }}>
              <div data-phi style={{ fontWeight: 600, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.patientName}</div>
              <div data-phi style={{ fontSize: '12px', color: '#6b716d' }}>ID: {row.patientId}</div>
            </div>
            <ClaimSelect
              ariaLabel={`Claim for ${row.patientName}`}
              claims={entry.claims}
              loading={entry.loading}
              value={row.claim}
              onChange={(value) => update(row.key, 'claim', value)}
              takenClaims={claimsInUse}
              style={input}
            />
            <div style={{ position: 'relative' }}>
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                  color: '#6b716d', fontSize: '14px', pointerEvents: 'none',
                }}
              >
                $
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                placeholder="0.00"
                aria-label={`Amount for ${row.patientName}`}
                value={row.amount}
                onChange={(e) => update(row.key, 'amount', e.target.value)}
                style={{ ...input, paddingLeft: '22px', textAlign: 'right' }}
              />
            </div>
            <button
              type="button"
              aria-label={`Remove ${row.patientName}`}
              title={`Remove ${row.patientName}`}
              onClick={() => remove(row.key)}
              style={{
                width: '36px',
                height: '36px',
                boxSizing: 'border-box',
                backgroundColor: '#b42318',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        );
      })}
      <div style={{ fontSize: '12px', color: '#6b716d', marginTop: '4px' }}>
        {autoPostOn
          ? 'Each patient with a claim posts to the PMS as its own insurance payment on that claim.'
          : 'Insurance auto-posting is off in Settings, so post each patient’s payment in the PMS yourself.'}
      </div>
    </div>
  );
};

export default InsuranceAllocations;
